import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import ActionColumn from '../components/ActionColumn';
import ACLEffectiveUserList, {
  type EffectiveUserRow,
} from '../features/acls/components/ACLEffectiveUserList';
import ACLInfo from '../features/acls/components/ACLInfo';
import ACLRoleList from '../features/acls/components/ACLRoleList';
import ACLRoleSelect from '../features/acls/components/ACLRoleSelect';
import ACLSelect from '../features/acls/components/ACLSelect';
import ACLSubmitChanges from '../features/acls/components/ACLSubmitChanges';
import CreateACLGroup from '../features/acls/components/CreateACLGroup';
import RefreshACLData from '../features/acls/components/RefreshACLData';
import { deriveEffectiveUsers } from '../features/acls/deriveEffectiveUsers';
import { isOnPremGroup } from '../features/acls/isOnPremGroup';
import {
  clearACLPendingRoles,
  addRoleToACLPendingAdds,
  selectACLPendingAdds,
  selectACLPendingDeletes,
} from '../features/acls/services/aclMembershipSlice';
import {
  useGetACLGroupsQuery,
  useGetACLGroupRoleMembersQuery,
  useGetACLGroupDirectUserMembersQuery,
  useGetRolesUserMembersQuery,
  useGetACLWritebackMembershipQuery,
  useGetRoleGroupsQuery,
} from '../features/graph/services/graphApi';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import { useSelectedGroup } from '../hooks/useSelectedGroup';
import type { RBACGroup } from '../types';

// Submit patches the role-members cache optimistically; the transitive count / effective
// users are server-computed and lag the write, so a manual Refresh is the ADR-0002 escape
// hatch to pull server truth. Hidden for now — flip to true to surface the button. The
// component (RefreshACLData) is fully wired so unhiding is just this flag.
const SHOW_REFRESH_BUTTON = false;

export default function ACLs() {
  const { aclId } = useParams<{ aclId: string }>();
  const dispatch = useAppDispatch();
  // On-prem-mastered groups are hidden from the ACL picker unless this is toggled on.
  const [showOnPrem, setShowOnPrem] = useState(false);

  const { data: aclData, isFetching: aclDataIsFetching } = useGetACLGroupsQuery();
  const { data: roleGroupData, isFetching: roleGroupDataIsFetching } = useGetRoleGroupsQuery();
  // Resolve the selected ACL from the list, falling back to a direct fetch (e.g. an
  // on-prem group filtered out of getACLGroups); returns undefined once the search is
  // cleared so ACLInfo + panels close. See useSelectedGroup.
  const acl = useSelectedGroup(aclId, aclData);
  const isOnPrem = isOnPremGroup(acl);

  // The ACL picker options: hide on-prem-mastered groups unless the toggle is on.
  const aclOptions = useMemo(
    () => (showOnPrem ? aclData : aclData?.filter((a) => !isOnPremGroup(a))),
    [aclData, showOnPrem]
  );

  const { data: roleMembers, isFetching: roleMembersIsFetching } = useGetACLGroupRoleMembersQuery(
    aclId ?? '',
    { skip: !aclId }
  );

  const { data: directUsers, isFetching: directUsersIsFetching } =
    useGetACLGroupDirectUserMembersQuery(aclId ?? '', { skip: !aclId });

  // Skip the cloud→AD writeback check for on-prem groups — it doesn't apply to them.
  const { data: inWriteback, isFetching: inWritebackIsFetching } =
    useGetACLWritebackMembershipQuery(aclId ?? '', { skip: !aclId || isOnPrem });

  const pendingAdds = useAppSelector(selectACLPendingAdds);
  const pendingDeletes = useAppSelector(selectACLPendingDeletes);
  const [roleWarning, setRoleWarning] = useState<string | null>(null);

  // User membership of the ACL's assigned roles plus any queued adds — used to
  // attribute each effective user to a role and to preview a pending add's new users.
  const allRoleIds = useMemo(() => {
    const ids = new Set<string>();
    (roleMembers ?? []).forEach((role) => ids.add(role.id));
    pendingAdds.forEach((role) => ids.add(role.id));
    return Array.from(ids).sort();
  }, [roleMembers, pendingAdds]);

  const { data: roleUsers, isFetching: roleUsersIsFetching } = useGetRolesUserMembersQuery(
    allRoleIds,
    { skip: allRoleIds.length === 0 }
  );

  // Effective-access rows: current members attributed to their role(s) or "Direct", plus
  // (green) users a queued role-add would newly grant access to. Derived purely from the
  // direct users + per-role memberships (no transitiveMembers server read), so it tracks
  // role add/remove instantly — see deriveEffectiveUsers. Computed for on-prem ACLs too:
  // they nest Role groups just like cloud ACLs (they're just read-only), so we surface
  // the full picture — directly-assigned users and everyone reached via a nested role.
  const effectiveRows = useMemo<EffectiveUserRow[]>(
    () =>
      deriveEffectiveUsers({
        directUsers,
        pendingAdds,
        pendingDeletes,
        roleMembers,
        roleUsers,
      }),
    [directUsers, roleMembers, pendingAdds, pendingDeletes, roleUsers]
  );

  // Any of the three lists the effective set is derived from still loading.
  const effectiveListIsFetching =
    roleMembersIsFetching || roleUsersIsFetching || directUsersIsFetching;

  // The committed member count in the ACL header, derived from the same data as the
  // effective list (excluding green pending-add previews) so the two never disagree.
  const effectiveUserCount = effectiveRows.filter((row) => !row.pending).length;
  const effectiveCountIsFetching = effectiveListIsFetching;

  // Queue a role for adding, but block duplicates: if it's already an assigned member
  // or already queued, do nothing and warn instead (prevents double rows / dup keys).
  const handleAddRole = (group: RBACGroup) => {
    if (roleMembers?.some((r) => r.id === group.id)) {
      setRoleWarning(`${group.displayName} is already assigned to this ACL.`);
      return;
    }
    if (pendingAdds.some((r) => r.id === group.id)) {
      setRoleWarning(`${group.displayName} is already queued to be added.`);
      return;
    }
    setRoleWarning(null);
    dispatch(addRoleToACLPendingAdds(group));
  };

  // Clear pending changes (and the warning) when the selected ACL changes
  useEffect(() => {
    dispatch(clearACLPendingRoles());
    setRoleWarning(null);
  }, [aclId, dispatch]);

  return (
    <div className="w-full px-6">
      <div className="flex flex-row">
        <div className="w-full">
          <div className="flex flex-row flex-wrap items-center gap-4 bg-slate-50 dark:bg-gray-800 rounded-lg w-full px-4 py-2">
            {/* left: the search controls */}
            <div className="flex flex-row gap-4">
              {aclDataIsFetching || roleGroupDataIsFetching ? (
                'Loading...'
              ) : (
                <>
                  <div className="w-72">
                    <ACLSelect acls={aclOptions} selectedId={aclId} />
                    {/* Mirrors the Users page toggles: switch on the left, label text to its right. */}
                    <label className="cursor-pointer label justify-start gap-2">
                      <input
                        type="checkbox"
                        className="toggle toggle-primary toggle-sm"
                        checked={showOnPrem}
                        onChange={() => setShowOnPrem(!showOnPrem)}
                      />
                      <span className="text-xs">Show on-prem groups</span>
                    </label>
                  </div>
                  <div className="w-72">
                    <ACLRoleSelect
                      groups={roleGroupData}
                      onGroupClick={handleAddRole}
                      enabled={!!acl}
                      readOnly={isOnPrem}
                      warning={roleWarning ?? undefined}
                    />
                  </div>
                </>
              )}
            </div>
            {/* middle: ACL details */}
            <div className="w-96">
              <ACLInfo
                acl={acl}
                userCount={effectiveUserCount}
                userCountIsFetching={effectiveCountIsFetching}
                inWriteback={inWriteback}
                inWritebackIsFetching={inWritebackIsFetching}
                isOnPrem={isOnPrem}
              />
            </div>
            <ActionColumn>
              <CreateACLGroup />
              <ACLSubmitChanges acl={acl} readOnly={isOnPrem} />
              {SHOW_REFRESH_BUTTON && aclId && (
                <RefreshACLData aclId={aclId} roleIds={allRoleIds} />
              )}
            </ActionColumn>
          </div>
        </div>
      </div>
      <div className="pt-4 pb-4">
        {aclId && (
          // Same role-list + effective-users view for cloud and on-prem ACLs — on-prem
          // just renders read-only (no role delete control; the role-add picker and
          // Submit are already gated above). See isOnPremGroup.
          <div className="grid items-stretch gap-4 lg:grid-cols-2">
            {roleMembersIsFetching ? (
              <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-4 text-sm opacity-60">
                Loading role members...
              </div>
            ) : (
              <ACLRoleList groups={roleMembers} readOnly={isOnPrem} />
            )}
            <ACLEffectiveUserList rows={effectiveRows} isFetching={effectiveListIsFetching} />
          </div>
        )}
      </div>
    </div>
  );
}
